import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
  MenuItem, Paper, Select, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { OrgDomainInfo } from '../context/UserContext';
import { ROLE_LABELS, useOrgApi } from '../utils/orgApi';
import { ConstructionLoader } from '../components/ConstructionLoader';

/**
 * Founders only (route gated on REACT_APP_ADMIN_EMAILS, API on requires_admin):
 * each firm's sign-in settings, which depend on Auth0 setup done by
 * UnderwritRE (enterprise batch 10, scripts/auth0/README.md). Every change is
 * written to that firm's Activity log. Batch 11 adds plan, seats and billing.
 */

interface Firm {
  id: string;
  name: string;
  plan_tier: string;
  billing_mode: string;
  seats_used: number;
  seat_limit: number | null;
  admin_emails: string[];
  require_mfa: boolean;
  sso_connection: string | null;
  sso_enforced: boolean;
  domains: OrgDomainInfo[];
}

const ROLES = ['member', 'viewer', 'admin'];

const AdminFirms: React.FC = () => {
  const api = useOrgApi();
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [firms, setFirms] = useState<Firm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<Firm | null>(null);
  const [connDraft, setConnDraft] = useState('');
  const [domainDraft, setDomainDraft] = useState('');
  const [roleDraft, setRoleDraft] = useState('member');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (search: string) => {
    setLoading(true);
    const r = await api<Firm[]>('GET', `/admin/orgs${search ? `?q=${encodeURIComponent(search)}` : ''}`);
    setLoading(false);
    if (!r.ok) {
      setError(r.error || 'Could not load firms.');
      return;
    }
    setFirms(r.data || []);
  }, [api]);

  useEffect(() => {
    load(term);
  }, [term, load]);

  const open = (f: Firm) => {
    setEditing(f);
    setConnDraft(f.sso_connection || '');
    setDomainDraft('');
    setRoleDraft('member');
    setError(null);
  };

  const replace = (f: Firm) => {
    setFirms((prev) => prev.map((x) => (x.id === f.id ? f : x)));
    setEditing(f);
  };

  const patch = async (body: Record<string, unknown>, msg: string) => {
    if (!editing) return;
    setBusy(true);
    const r = await api<Firm>('PATCH', `/admin/orgs/${editing.id}`, body);
    setBusy(false);
    if (!r.ok) {
      setError(r.error || 'Could not save.');
      return;
    }
    replace({ ...editing, ...r.data });
    setNotice(msg);
  };

  const addDomain = async () => {
    if (!editing || !domainDraft.trim()) return;
    setBusy(true);
    const r = await api<OrgDomainInfo>('POST', `/admin/orgs/${editing.id}/domains`,
      { domain: domainDraft.trim(), default_role: roleDraft });
    setBusy(false);
    if (!r.ok) {
      setError(r.error || 'Could not add the domain.');
      return;
    }
    replace({ ...editing, domains: editing.domains.concat([r.data]) });
    setDomainDraft('');
    setNotice(`${r.data.domain} now belongs to ${editing.name}.`);
  };

  const removeDomain = async (d: OrgDomainInfo) => {
    if (!editing) return;
    setBusy(true);
    const r = await api('DELETE', `/admin/orgs/${editing.id}/domains/${d.id}`);
    setBusy(false);
    if (!r.ok) {
      setError(r.error || 'Could not remove the domain.');
      return;
    }
    replace({ ...editing, domains: editing.domains.filter((x) => x.id !== d.id) });
    setNotice(`${d.domain} removed.`);
  };

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', p: 2 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>Firms</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sign-in settings that need UnderwritRE: single sign-on connections and company domains. Firm admins
        see them on their Team page, and every change here appears in that firm's Activity log.
      </Typography>
      {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice}</Alert>}
      {error && !editing && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          size="small"
          label="Firm name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') setTerm(q.trim()); }}
          sx={{ width: 260 }}
        />
        <Button variant="outlined" size="small" onClick={() => setTerm(q.trim())}>Search</Button>
      </Box>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><ConstructionLoader /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Firm</TableCell>
                  <TableCell sx={{ width: 90 }}>People</TableCell>
                  <TableCell sx={{ width: 110 }}>Plan</TableCell>
                  <TableCell>Admins</TableCell>
                  <TableCell sx={{ width: 220 }}>Sign-in</TableCell>
                  <TableCell sx={{ width: 120 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {firms.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell sx={{ fontWeight: 600 }}>{f.name}</TableCell>
                    <TableCell>{f.seat_limit ? `${f.seats_used} / ${f.seat_limit}` : f.seats_used}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      {f.plan_tier}{f.billing_mode === 'invoice' ? ' (invoiced)' : ''}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>{f.admin_emails.join(', ')}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {f.require_mfa && <Chip size="small" label="2FA" />}
                        {f.sso_connection && <Chip size="small" label={f.sso_enforced ? 'SSO required' : 'SSO'} />}
                        {f.domains.length > 0 && <Chip size="small" label={`${f.domains.length} domain${f.domains.length === 1 ? '' : 's'}`} />}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => open(f)}>Sign-in settings</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.name}: sign-in settings</DialogTitle>
        {editing && (
          <DialogContent>
            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

            <Typography variant="subtitle2" sx={{ mt: 1 }}>Single sign-on</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The Auth0 enterprise connection name, exactly as in the Auth0 dashboard (Authentication &gt; Enterprise).
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField size="small" fullWidth placeholder="e.g. blue-harbor-okta" value={connDraft}
                onChange={(e) => setConnDraft(e.target.value)} inputProps={{ maxLength: 128 }} />
              <Button variant="outlined" disabled={busy || connDraft.trim() === (editing.sso_connection || '')}
                onClick={() => patch({ sso_connection: connDraft.trim() }, connDraft.trim() ? 'SSO connection saved.' : 'SSO connection removed.')}>
                Save
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch checked={editing.sso_enforced} disabled={busy || !editing.sso_connection}
                onChange={(e) => patch({ sso_enforced: e.target.checked },
                  e.target.checked ? 'SSO is now required for this firm.' : 'SSO is now optional for this firm.')} />
              <Typography variant="body2">
                Require SSO (turn on only after someone has signed in through the connection successfully)
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2">Two-factor sign-in</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch checked={editing.require_mfa} disabled={busy || !editing.require_mfa}
                onChange={(e) => patch({ require_mfa: e.target.checked }, 'Two-factor is now optional for this firm.')} />
              <Typography variant="body2">
                {editing.require_mfa
                  ? 'Required. Switch off only to unlock a firm that cannot sign in.'
                  : 'Optional. The firm\'s admins turn this on from their Team page, once two-factor is live (scripts/auth0/README.md).'}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2">Company domains</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Confirm a domain only when you know the firm owns it. A new sign-up at the domain joins this firm
              with the role chosen here, when the address is verified or comes through the firm's SSO.
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
              {editing.domains.length === 0 && <Typography variant="body2" color="text.secondary">None yet.</Typography>}
              {editing.domains.map((d) => (
                <Chip key={d.id} label={`${d.domain} (${ROLE_LABELS[d.default_role] || d.default_role})`}
                  onDelete={busy ? undefined : () => removeDomain(d)} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" placeholder="acmecapital.com" value={domainDraft}
                onChange={(e) => setDomainDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addDomain(); }} sx={{ flex: 1 }} />
              <Select size="small" value={roleDraft} onChange={(e) => setRoleDraft(e.target.value as string)} sx={{ minWidth: 120 }}>
                {ROLES.map((r) => <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>)}
              </Select>
              <Button variant="outlined" disabled={busy || !domainDraft.trim()} onClick={addDomain}>Add</Button>
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          <Tooltip title="Close">
            <IconButton onClick={() => setEditing(null)} aria-label="close"><CloseIcon /></IconButton>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminFirms;
